import { useEffect, type ReactNode } from 'react';
import { PageLoader } from '@/components/feedback/PageLoader';
import { errorStatus, useMarketplaceAccess } from './api/useMarketplaceAccess';
import { EntitlementContext } from './EntitlementContext';
import { startLogin } from './ssoClient';
import { SubscriptionRequired } from './SubscriptionRequired';

interface EntitlementGuardProps {
  children: ReactNode;
}

/**
 * Gates the app shell on an active Azure Marketplace entitlement
 * (GET /sso-api/marketplace/access). Sits inside `<AuthGuard>`:
 *   200 → render the app with the entitlement in context
 *   401 → session expired → SSO sign-in
 *   403 → no active entitlement → full-page SubscriptionRequired
 */
export function EntitlementGuard({ children }: EntitlementGuardProps) {
  const { data, error, isPending, refetch } = useMarketplaceAccess();
  const status = errorStatus(error);

  useEffect(() => {
    if (status === 401) startLogin();
  }, [status]);

  // 401/403 win over cached data: a refetch that finds the subscription
  // suspended must block immediately.
  if (status === 401) return <PageLoader label="Redirecting to sign-in…" />;
  if (status === 403) return <SubscriptionRequired />;

  if (data) {
    return <EntitlementContext.Provider value={data}>{children}</EntitlementContext.Provider>;
  }

  if (isPending) return <PageLoader label="Checking subscription…" />;

  // Network failure / schema drift with nothing cached. Rendered here rather
  // than thrown because the app's ErrorBoundary lives below this guard.
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
      <p className="text-lg font-semibold text-white">We couldn’t verify your subscription.</p>
      <p className="max-w-md text-sm text-white/70">{error?.message}</p>
      <button
        type="button"
        onClick={() => void refetch()}
        className="rounded-lg bg-brand-orange px-4 py-2 text-sm font-medium text-white hover:opacity-95"
      >
        Try again
      </button>
    </div>
  );
}
